import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Check, AlertCircle, Loader2, Send, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Logo, Button } from '../components';
import { inputCls, labelCls, cardCls } from './publicFormStyles';

/**
 * Reference form — PUBLIC page at /reference/:token
 *
 * Filled in by somebody OUTSIDE the organisation, about somebody else. Three
 * things follow from that, and they shape the whole page:
 *
 *   They are told what this is before they are asked anything. A referee did
 *   not sign up for an account and has every reason to treat an unexpected form
 *   as a scam, so the header names the applicant, the organisation and the kind
 *   of reference being asked for.
 *
 *   They see NOTHING else about the applicant. No address, no documents, no
 *   other referees. The API sends only the name, the role and the questions.
 *
 *   There are no drafts. It is a five-minute form, and the link is single use —
 *   a half-finished reference behind a link nobody reopens is worse than none.
 *
 * No auth header is sent: the token in the URL IS the credential.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/*
 * NOTHING ON THIS FORM IS REQUIRED, and no field carries an asterisk.
 *
 * A referee is doing the organisation a favour. They are not its user, they did
 * not ask for this, and they may be able to confirm three things and nothing
 * else — an employer who cannot remember an end date, a neighbour with no view
 * on "following care plans". Demanding every box is how you turn a partial
 * honest reference into either an abandoned form or an invented answer, and an
 * invented answer in a safer-recruitment file is worse than a blank one.
 *
 * The manager reads what came back and decides whether it is enough; that
 * judgment is theirs, and it is not something a required-field list can make
 * for them. referenceSummary() already omits whatever was left empty.
 */

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#F5F9FF] dark:bg-[#1b1f26] px-4 py-10">
    <div className="max-w-[820px] mx-auto">
      <div className="mb-6 flex justify-center"><Logo /></div>
      {children}
    </div>
  </div>
);

const ReferenceForm = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState('');
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (patch) => setAnswers((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/recruitment/reference/${token}`);
        const body = await response.json();
        if (!active) return;
        if (!response.ok || !body.success) {
          setFatal(body.message || 'This link could not be opened.');
          return;
        }
        setData(body.data);
        setAnswers({
          ...body.data.answers,
          // The candidate gave us these from memory; the referee is the
          // authority on their own name and address, so they are editable.
          referee_name: body.data.reference.referee_name || '',
          referee_email: body.data.reference.referee_email || '',
        });
        if (body.data.submitted) setDone({ already: true });
      } catch {
        if (active) setFatal('We could not reach the server. Please check your connection and try again.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/recruitment/reference/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(answers),
      });
      const body = await response.json();

      if (!response.ok || !body.success) {
        toast.error(body.message || 'Could not send your reference.');
        return;
      }
      setDone(body.data || {});
    } catch {
      toast.error('We could not reach the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className={`${cardCls} text-center`}>
          <Loader2 size={30} className="mx-auto animate-spin text-blue-600" />
        </div>
      </Shell>
    );
  }

  if (fatal) {
    return (
      <Shell>
        <div className={`${cardCls} text-center`}>
          <AlertCircle size={34} className="mx-auto text-amber-500 mb-3" />
          <h1 className="text-[17px] font-bold text-[#1E293B] dark:text-gray-100 mb-2">This link can&apos;t be opened</h1>
          <p className="text-[14px] text-[#64748B] dark:text-gray-400">{fatal}</p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className={`${cardCls} text-center`}>
          <div className="w-14 h-14 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center mx-auto mb-4">
            <Check size={28} className="text-emerald-600" />
          </div>
          <h1 className="text-[20px] font-bold text-[#1E293B] dark:text-gray-100">
            {done.already ? 'This reference has already been sent' : 'Thank you'}
          </h1>
          <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-2">
            {done.already
              ? 'There is nothing more you need to do.'
              : `Your reference for ${data.applicant.name} has been sent to ${data.organisation.name}. There is nothing more you need to do.`}
          </p>
        </div>
      </Shell>
    );
  }

  const { applicant, organisation, reference, questions } = data;

  /** One labelled input, of whatever kind the spec says. */
  const field = (spec, { full = false } = {}) => (
    <div key={spec.name || spec.field} className={full ? 'sm:col-span-2' : ''}>
      <label className={labelCls} htmlFor={spec.name || spec.field}>{spec.label}</label>
      {spec.type === 'select' ? (
        <select
          id={spec.name || spec.field}
          className={inputCls}
          value={answers[spec.name || spec.field] || ''}
          onChange={(event) => set({ [spec.name || spec.field]: event.target.value })}
        >
          <option value="">Select an answer</option>
          {spec.options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      ) : (
        <input
          id={spec.name || spec.field}
          className={inputCls}
          placeholder={spec.placeholder || ''}
          value={answers[spec.name || spec.field] || ''}
          onChange={(event) => set({ [spec.name || spec.field]: event.target.value })}
        />
      )}
      {spec.hint && <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">{spec.hint}</p>}
    </div>
  );

  return (
    <Shell>
      <div className="space-y-5">
        {/* Who is asking, and why. Before anything is asked of them. */}
        <div className={cardCls}>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/20 rounded-2xl flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-[20px] font-bold text-[#1E293B] dark:text-gray-100">
                Reference for {applicant.name}
              </h1>
              <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-1">
                {applicant.name} has applied to join {organisation.name}
                {applicant.role_applied_for ? ` as a ${applicant.role_applied_for}` : ''} and named you as a
                referee. We are asking for a <strong className="text-[#1E293B] dark:text-gray-200">
                  {reference.type_label.toLowerCase()}
                </strong> reference.
              </p>
              <p className="text-[12.5px] text-[#94A3B8] dark:text-gray-500 mt-2">
                It takes around 5 minutes. You will not see the rest of their application, and this
                link can be used once.
              </p>
            </div>
          </div>
        </div>

        {/* Your details. An employment referee is also asked who they work for;
            a character referee may have no relevant organisation at all, so the
            spec decides. */}
        <div className={cardCls}>
          <h2 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-100 mb-4">Your details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} htmlFor="referee_name">Full name</label>
              <input id="referee_name" className={inputCls} value={answers.referee_name || ''} onChange={(e) => set({ referee_name: e.target.value })} />
            </div>
            <div>
              <label className={labelCls} htmlFor="referee_email">Email address</label>
              <input id="referee_email" type="email" className={inputCls} value={answers.referee_email || ''} onChange={(e) => set({ referee_email: e.target.value })} />
            </div>
            {questions.detailFields.map((spec) => field(spec))}
            {/* An employment referee's "how do you know them" belongs with their
                job details; a character referee gets a section of its own. */}
            {questions.asksEmployment && field(questions.howKnown, { full: true })}
          </div>
        </div>

        {/* How you know <first name> — character references only. What an
            employer's answer implies (how long, in what capacity) has to be
            asked outright of somebody who knows them personally. */}
        {!questions.asksEmployment && (
          <div className={cardCls}>
            <h2 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-100 mb-4">
              {questions.contextTitle}
            </h2>
            <div className="grid grid-cols-1 gap-4">
              {field(questions.howKnown)}
              {questions.extraContext.map((spec) => field(spec))}
            </div>
          </div>
        )}

        {/* Employment details — an employment referee only. A character referee
            cannot confirm dates, and a guess in a recruitment file is worse
            than a blank. */}
        {questions.asksEmployment && (
          <div className={cardCls}>
            <h2 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-100 mb-4">Employment details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="applicant_job_title">Applicant&apos;s job title</label>
                <input id="applicant_job_title" className={inputCls} value={answers.applicant_job_title || ''} onChange={(e) => set({ applicant_job_title: e.target.value })} />
              </div>
              <div>
                <label className={labelCls} htmlFor="employment_start">Employment start date</label>
                <input id="employment_start" type="date" className={inputCls} value={answers.employment_start || ''} onChange={(e) => set({ employment_start: e.target.value })} />
              </div>
              <div>
                <label className={labelCls} htmlFor="employment_end">Employment end date</label>
                <input
                  id="employment_end" type="date" className={inputCls}
                  value={answers.employment_end || ''}
                  disabled={!!answers.still_employed}
                  onChange={(e) => set({ employment_end: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 text-[13px] font-semibold text-[#475569] dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 accent-blue-600"
                    checked={!!answers.still_employed}
                    onChange={(e) => set({ still_employed: e.target.checked, employment_end: e.target.checked ? '' : answers.employment_end })}
                  />
                  Still employed by this organisation
                </label>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="reason_for_leaving">
                  Reason for leaving <span className="text-[#94A3B8] font-normal">(if known)</span>
                </label>
                <input id="reason_for_leaving" className={inputCls} value={answers.reason_for_leaving || ''} onChange={(e) => set({ reason_for_leaving: e.target.value })} />
              </div>
            </div>
          </div>
        )}

        {/* The reference itself. Title, blurb, ratings and the closing question
            all come from the type — an employer is asked about the work, a
            character referee about the person. */}
        <div className={cardCls}>
          <h2 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-100">{questions.section.title}</h2>
          <p className="text-[13px] text-[#64748B] dark:text-gray-400 mt-1 mb-5">{questions.section.blurb}</p>

          <div className="space-y-4">
            {questions.rated.map((question) => (
              <div key={question.name} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <label className="text-[13.5px] text-[#1E293B] dark:text-gray-200 sm:max-w-[55%]" htmlFor={question.name}>
                  {question.label}
                </label>
                <select
                  id={question.name}
                  className={`${inputCls} sm:w-[280px]`}
                  value={answers[question.name] || ''}
                  onChange={(event) => set({ [question.name]: event.target.value })}
                >
                  <option value="">Select an answer</option>
                  {questions.ratings.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-[#F1F5F9] dark:border-gray-800 space-y-5">
            {field({ ...questions.concerns, name: questions.concerns.field, type: 'select' }, { full: true })}
            {field({ ...questions.closing, name: questions.closing.field, type: 'select' }, { full: true })}

            <div>
              <label className={labelCls} htmlFor="additional_comments">
                Anything else you would like to add? <span className="text-[#94A3B8] font-normal">(optional)</span>
              </label>
              <textarea
                id="additional_comments"
                rows={4}
                className={inputCls}
                placeholder="Relevant information about their work or your answers above"
                value={answers.additional_comments || ''}
                onChange={(e) => set({ additional_comments: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Declaration */}
        <div className={cardCls}>
          <h2 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-100 mb-3">Declaration</h2>
          <label className="flex items-start gap-2.5 text-[13.5px] text-[#475569] dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 mt-0.5 rounded border-gray-300 accent-blue-600"
              checked={!!answers.declaration}
              onChange={(e) => set({ declaration: e.target.checked })}
            />
            I confirm I am authorised to provide this reference and the information is accurate to the
            best of my knowledge.
          </label>
          <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-2 ml-6">
            Your response is intended for the recruiting organisation to support its recruitment checks.
          </p>
        </div>

        <div className={`${cardCls} flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3`}>
          <p className="text-[12.5px] text-[#64748B] dark:text-gray-400">
            Answer what you can — nothing here is required. Please check your answers before submitting.
          </p>
          <Button onClick={submit} loading={submitting} leftIcon={<Send size={15} />} className="w-full sm:w-auto shrink-0">
            Submit reference
          </Button>
        </div>
      </div>
    </Shell>
  );
};

export default ReferenceForm;
